import { signal } from '@angular/core';

/**
 * Ein Wert je Schritt, reaktiv gelesen.
 *
 * <p>Der Runner haelt mehrere solcher Karten — Entwuerfe, Kommentar-Entwuerfe, den
 * Bug-Schluessel und seinen Bearbeitungszustand. Alle aus demselben Grund: ein Wechsel
 * in der Schrittliste darf nichts mitnehmen und nichts am falschen Schritt abschicken.
 * Ohne diese Klasse stand das Schreibmuster {@code update((m) => ({ ...m, [id]: v }))}
 * an jeder einzelnen Stelle erneut.
 *
 * <p>Die Live-Ansicht schluesselt dieselbe Karte auf das Fall-Ergebnis statt auf den
 * Schritt — dort ist die Zeile der Fall.
 */
export class StepMap<T> {
  private readonly entries = signal<Record<string, T>>({});

  /** Reaktiv: ein Aufrufer in einem {@code computed} folgt jeder Aenderung. */
  get(stepId: string): T | undefined {
    return this.entries()[stepId];
  }

  set(stepId: string, value: T): void {
    this.entries.update((entries) => ({ ...entries, [stepId]: value }));
  }

  remove(stepId: string): void {
    this.entries.update((entries) => {
      const rest = { ...entries };
      delete rest[stepId];
      return rest;
    });
  }

  retain(stepIds: ReadonlySet<string>): void {
    const current = Object.entries(this.entries());
    const kept = current.filter(([stepId]) => stepIds.has(stepId));
    if (kept.length < current.length) this.entries.set(Object.fromEntries(kept));
  }

  /** Ob ueberhaupt ein Schritt einen Wert traegt — die Dirty-Erkennung haengt daran. */
  get empty(): boolean {
    return Object.keys(this.entries()).length === 0;
  }
}
